import { useEffect, useState } from "react";
import type { ViewerMemberCommunicationsResponseDto } from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { fetchFamilyMemberCommunications } from "../../shared/api/memberCommunication";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { canUseFamilyMode } from "../../shared/session/modeRouting";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { FamilyAppShell } from "../../shared/ui/family/FamilyAppShell";
import { MemberCommunicationCard } from "../../shared/ui/MemberCommunicationCard";
import { DEFAULT_TENANT_TIMEZONE } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";

export function FamilyMemberCommunicationsPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [communications, setCommunications] = useState<ViewerMemberCommunicationsResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const hasFamilyAccessRole = canUseFamilyMode(session);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasFamilyAccessRole) {
      return;
    }

    let cancelled = false;

    async function loadCommunications() {
      setLoadState("loading");
      setLoadError(null);

      try {
        const nextCommunications = await fetchFamilyMemberCommunications(tenantSlug);
        if (cancelled) {
          return;
        }
        setCommunications(nextCommunications);
        setLoadState("idle");
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }
        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, "連絡事項の取得に失敗しました。"));
      }
    }

    void loadCommunications();

    return () => {
      cancelled = true;
    };
  }, [hasFamilyAccessRole, isSessionReady, navigate, tenantSlug]);

  return (
    <FamilyAppShell
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      activeTab="communications"
      navigationCountsEnabled={hasFamilyAccessRole}
      {...(communications ? { navigationCountsSeed: { communications: communications.unreadCount } } : {})}
    >
      <div className="family-page-stack">
        {routeStatus === "checking" || sessionStatus === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">セッションを確認しています。</p>
          </article>
        ) : null}
        {routeStatus === "redirecting" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">利用開始へ移動しています。</p>
          </article>
        ) : null}
        {sessionStatus === "error" ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">セッション確認に失敗しました。{errorMessage}</p>
          </article>
        ) : null}
        {routeStatus === "authenticated" && !hasFamilyAccessRole ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">この画面は保護者または{tenantLabels.student}本人のみ利用できます。</p>
          </article>
        ) : null}

        <section className="family-surface family-section">
          <h2 className="family-page-title">連絡事項</h2>
          <p className="family-page-intro">クラブや{tenantLabels.teacher}から届いた連絡を確認できます。</p>
        </section>

        {loadState === "loading" ? (
          <article className="family-surface family-notice">
            <p className="family-text-muted">連絡事項を読み込んでいます。</p>
          </article>
        ) : null}
        {loadError ? (
          <article className="family-surface family-notice family-notice--warning">
            <p className="family-text-muted">{loadError}</p>
          </article>
        ) : null}

        {communications?.items.length === 0 ? (
          <article className="family-surface family-notice">
            <p className="family-empty-state">連絡事項はまだありません。</p>
          </article>
        ) : null}

        {communications?.items.length ? (
          <section className="family-surface family-section">
            <div className="family-list family-list--compact">
              {communications.items.map((communication) => (
                <MemberCommunicationCard
                  key={communication.communicationId}
                  communication={communication}
                  to={`/t/${tenantSlug}/member-communications/${communication.communicationId}`}
                  timeZone={DEFAULT_TENANT_TIMEZONE}
                  tenantSlug={tenantSlug}
                  thumbnailSurface="family"
                  labels={tenantLabels}
                  enableFamilyAttachmentOpen
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </FamilyAppShell>
  );
}
